import type { TrafficLightType } from '@scaleaq/scaleui';

import {
  StreamHealthState,
  StreamPacketStatsState,
  type SourceStreamPacketStats,
  type WebRtcStream,
  type WebRtcStreamStatus,
} from '@scaleaq/sitecontrol-lib';

import {
  CLIENT_JITTER_CRITICAL_MS,
  CLIENT_JITTER_DEGRADED_MS,
  CLIENT_PACKET_LOSS_CRITICAL_PERCENT,
  CLIENT_PACKET_LOSS_DEGRADED_PERCENT,
  CLIENT_PLI_DEGRADED_COUNT,
  SOURCE_STATUS_STALE_AFTER_MS,
} from '@/constants';

/** Verdict on one leg of the video path, in rising severity. */
export type StreamLegLevel = 'healthy' | 'degraded' | 'critical';

/** Which leg drives a non-healthy combined light. */
export type StreamHealthCause = 'both' | 'client' | 'source';

/** Browser-side WebRTC readings for the mediaserver → browser leg. */
export type ClientStreamStats = {
  clientJitterMs: number;
  clientPacketLossPercent: number;
  /** Shown, not judged: no threshold yet (see docs/stream-jitter-thresholds.md). */
  clientNackPercent?: number;
  /** Keyframe requests over the stats window; any at all is Caution. */
  clientPliCount?: number;
};

export type SourceStatAbsentReasonKey =
  | 'common:notAvailable'
  | 'networkHealth:noReadingReported'
  | `networkHealth:statsState.${StreamPacketStatsState}`;

export type SourceStatPresence =
  | { kind: 'value'; value: number }
  | { kind: 'absent'; reasonKey: SourceStatAbsentReasonKey };

export type CameraStreamHealth = {
  trafficLight: TrafficLightType;
  cause?: StreamHealthCause;
};

type SourceStatPresenceArgs = {
  sourceStats?: SourceStreamPacketStats;
  value?: number | null;
};

type FindStreamStatusArgs = {
  webRtcStreams?: WebRtcStream[];
  streamName?: string;
};

type SourceStatusStaleArgs = {
  lastUpdated?: string;
  nowMs: number;
};

type CameraStreamHealthArgs = {
  connectionState: RTCPeerConnectionState;
  isVideoPaused: boolean;
  isRewinding: boolean;
  clientStats?: ClientStreamStats;
  streamStatus?: WebRtcStreamStatus;
};

type CombineLegsArgs = {
  source?: StreamLegLevel;
  client?: StreamLegLevel;
};

/**
 * Whether a source reading can be shown, and if not, why. `0` is a real measurement, so
 * presence turns on null/undefined alone.
 */
export const getSourceStatPresence = ({
  sourceStats,
  value,
}: SourceStatPresenceArgs): SourceStatPresence => {
  if (!sourceStats) return { kind: 'absent', reasonKey: 'common:notAvailable' };

  // Ahead of the value on purpose. On a non-UDP transport neither loss nor jitter describes
  // the link, and the lifetime figure survives from the path's earlier UDP sessions - so a
  // value can be present here and still not be a reading of the stream in front of you.
  if (sourceStats.statsState === StreamPacketStatsState.UnsupportedTransport) {
    return { kind: 'absent', reasonKey: 'networkHealth:statsState.UnsupportedTransport' };
  }

  if (value != null) return { kind: 'value', value };

  // Available promises the counters are real, so it can never explain an absent figure.
  if (sourceStats.statsState === StreamPacketStatsState.Available) {
    return { kind: 'absent', reasonKey: 'networkHealth:noReadingReported' };
  }

  return { kind: 'absent', reasonKey: `networkHealth:statsState.${sourceStats.statsState}` };
};

/**
 * Maps to the operational colours in the design guide. Critical (red) is reserved for danger to
 * life and other emergencies, which no video stream is: a Critical stream is Alarm (orange), a
 * serious problem to act on, and Degraded is Caution (yellow). Closed is greyscale, for hardware
 * that is off without causing harm.
 */
export const getStreamHealthTrafficLight = (healthState: StreamHealthState): TrafficLightType => {
  switch (healthState) {
    case StreamHealthState.Healthy:
      return 'success';
    case StreamHealthState.Degraded:
      return 'caution';
    case StreamHealthState.Critical:
      return 'alarm';
    case StreamHealthState.Closed:
      return 'neutral';
    default:
      return 'neutral';
  }
};

/** Live status for a stream, read from the camera rather than a stored selection snapshot. */
export const findStreamStatus = ({
  webRtcStreams,
  streamName,
}: FindStreamStatusArgs): WebRtcStreamStatus | undefined =>
  streamName ? webRtcStreams?.find(s => s.name === streamName)?.streamStatus : undefined;

/** True when the source status is older than the stale limit. Unknown age is not stale. */
export const isSourceStatusStale = ({ lastUpdated, nowMs }: SourceStatusStaleArgs): boolean => {
  const updatedMs = lastUpdated ? Date.parse(lastUpdated) : NaN;
  return Number.isFinite(updatedMs) && nowMs - updatedMs > SOURCE_STATUS_STALE_AFTER_MS;
};

/**
 * Client verdict: loss and jitter on the same thresholds the mediaserver applies to the source,
 * plus keyframe requests (PLI), which only the browser can see.
 */
export const getClientLegLevel = ({
  clientJitterMs,
  clientPacketLossPercent,
  clientPliCount,
}: ClientStreamStats): StreamLegLevel => {
  if (
    clientPacketLossPercent >= CLIENT_PACKET_LOSS_CRITICAL_PERCENT ||
    clientJitterMs >= CLIENT_JITTER_CRITICAL_MS
  ) {
    return 'critical';
  }
  if (
    clientPacketLossPercent >= CLIENT_PACKET_LOSS_DEGRADED_PERCENT ||
    clientJitterMs >= CLIENT_JITTER_DEGRADED_MS ||
    (clientPliCount ?? 0) >= CLIENT_PLI_DEGRADED_COUNT
  ) {
    return 'degraded';
  }
  return 'healthy';
};

/**
 * Source verdict, or undefined when it has none to give. Closed beside a playing stream is
 * a status that has not caught up yet (on-demand sources start when the viewer connects).
 */
export const getSourceLegLevel = (
  streamStatus: WebRtcStreamStatus | undefined
): StreamLegLevel | undefined => {
  switch (streamStatus?.healthState) {
    case StreamHealthState.Healthy:
      return 'healthy';
    case StreamHealthState.Degraded:
      return 'degraded';
    case StreamHealthState.Critical:
      return 'critical';
    default:
      return undefined;
  }
};

/**
 * One light for both legs: camera → mediaserver (source) and mediaserver → browser (client).
 * The worse leg wins. Rewind plays over HLS, which has no client reading yet, so it shows
 * the source alone.
 */
export const getCameraStreamHealth = ({
  connectionState,
  isVideoPaused,
  isRewinding,
  clientStats,
  streamStatus,
}: CameraStreamHealthArgs): CameraStreamHealth => {
  const source = getSourceLegLevel(streamStatus);
  if (isRewinding) return combineLegs({ source });
  if (isVideoPaused) return { trafficLight: 'neutral' };

  switch (connectionState) {
    case 'connected':
      return combineLegs({
        source,
        client: clientStats ? getClientLegLevel(clientStats) : undefined,
      });
    case 'new':
      return { trafficLight: 'lime' };
    case 'connecting':
      return { trafficLight: 'info' };
    default:
      return { trafficLight: 'alarm' };
  }
};

const LEVEL_RANK: Record<StreamLegLevel, number> = {
  healthy: 0,
  degraded: 1,
  critical: 2,
};

const LEVEL_TRAFFIC_LIGHT: Record<StreamLegLevel, TrafficLightType> = {
  healthy: 'success',
  degraded: 'caution',
  critical: 'alarm',
};

const combineLegs = ({ source, client }: CombineLegsArgs): CameraStreamHealth => {
  const sourceRank = source ? LEVEL_RANK[source] : 0;
  const clientRank = client ? LEVEL_RANK[client] : 0;
  const worst = Math.max(sourceRank, clientRank);
  if (worst === 0) return { trafficLight: 'success' };

  const level = sourceRank === worst ? source : client;
  return {
    trafficLight: LEVEL_TRAFFIC_LIGHT[level ?? 'healthy'],
    cause: getCause({ sourceRank, clientRank }),
  };
};

const getCause = ({
  sourceRank,
  clientRank,
}: {
  sourceRank: number;
  clientRank: number;
}): StreamHealthCause => {
  if (sourceRank === clientRank) return 'both';
  return sourceRank > clientRank ? 'source' : 'client';
};
